import { Directive, ElementRef, OnInit, OnDestroy, Input } from '@angular/core';
import { Pair } from 'huddyapi';

export interface ECSNode<T> {
  value?: T;
  child?: ECStructure<T>;
}

export type ECStructure<T> = {
  [label: string]: ECSNode<T>[];
}

export abstract class ECPath {
  static toString(path: Pair<string, number>[]): string {
    return path.map((pair: Pair<string, number>): string => pair.a + (pair.b == null ? '' : '[' + pair.b.toString() + ']')).join('');
  }

  static parse(pathString: string): Pair<string, number>[] {
    pathString = pathString || '';
    let label: string = '', index: string = '', readingLabel: boolean = true, path: Pair<string, number>[] = [];
    for(let i = 0; i < pathString.length; i++) {
      const char: string = pathString.charAt(i);
      if(readingLabel) {
        if(char == '[')
          readingLabel = false;
        else
          label += char; 
      } else {
        if(char == ']') {
          path.push(new Pair<string, number>(label, parseInt(index)));
          label = '';
          index = '';
          readingLabel = true;
        } else
          index += char;
      }
    }
    if(label.length > 0)
      path.push(new Pair<string, number>(label, null));
    return path;
  }
}

export class ExpandCollapseController {
  private state: ECStructure<boolean>;
  private directives: ECStructure<CollapsibleDirective>;

  constructor(state?: ECStructure<boolean>) {
    this.directives = {};
    this.setState(state || {});
  }

  setState(state: ECStructure<boolean>) {
    this.state = state;
    const updateFunction: (state: ECStructure<boolean>, directives: ECStructure<CollapsibleDirective>) => void = (state: ECStructure<boolean>, directives: ECStructure<CollapsibleDirective>): void => {
      for(let label in state) {
        const directiveList: ECSNode<CollapsibleDirective>[] = (directives || {})[label] || [];
        state[label].forEach((item: ECSNode<boolean>, index: number) => {
          const directive: ECSNode<CollapsibleDirective> = directiveList ? directiveList[index] : null;
          if(item.value != null && directive?.value != null)
            directive.value.setExpanded(item.value);
          if(item.child != null)
            updateFunction(item.child, directive?.child);
        });
      }
    };
    updateFunction(this.state, this.directives);
  }

  getStateValue(path: Pair<string, number>[]): boolean {
    if(!(path?.length > 0))
      return null;
    let state: ECStructure<boolean> = this.state;
    for(let i = 0; i < path.length; i++) {
      const pair: Pair<string, number> = path[i];
      if(pair.b == null)
        return;
      let list: ECSNode<boolean>[] = state[pair.a];
      if(!list)
        return null;
      const next: ECSNode<boolean> = list[pair.b];
      if(next == null)
        return null;
      if(i < path.length - 1) {
        if(next.child == null)
          return null;
        state = next.child;
      } else
        return next.value;
    }
  }

  setStateValue(path: Pair<string, number>[], expanded: boolean, animate: boolean = false) {
    console.log('structure', this.state, 'path=', path);
    const stateList: ECSNode<boolean>[] = this.getList(this.state, path, false);
    console.log('stateList=', stateList);
    if(!stateList)
      return;
    const directiveList: ECSNode<CollapsibleDirective>[] = this.getList(this.directives, path, false) || [];
    const index: number = path[path.length - 1].b;
    if(index == null || index >= stateList.length)
      return;
    stateList[index].value = expanded;
    if(directiveList?.length > index && directiveList[index] != null)
      directiveList[index].value?.setExpanded(expanded, animate);
  }

  private getList<T>(structure: ECStructure<T>, path: Pair<string, number>[], create: boolean): ECSNode<T>[] {
    if(!(path?.length > 0) || !structure)
      return null;
    for(let i = 0; i < path.length - 1; i++) {
      const pair: Pair<string, number> = path[i];
      let list: ECSNode<T>[] = structure[pair.a];
      if(!list) {
        if(!create)
          return null;
        list = [];
        structure[pair.a] = list;
      }
      const item: ECSNode<T> = list[pair.b];
      if(item == null)
        return null;
      if(item.child == null) {
        if(!create)
          return null;
        item.child = {};
      }
      structure = item.child;
    }
    let result = structure[path[path.length - 1].a];
    if(!result) {
      if(!create)
        return null;
      result = [];
      structure[path[path.length - 1].a] = result;
    }
    return result;
  }

  private expandDirectiveNode(directiveNode: ECSNode<CollapsibleDirective>, expanded: boolean | ECSNode<boolean>): boolean {
    if(directiveNode == null || directiveNode.value == null)
      return false;
    if(typeof expanded == 'object')
      expanded = (expanded as ECSNode<boolean>).value;
    const result: boolean = directiveNode.value.getExpanded();
    directiveNode.value.setExpanded((expanded as boolean) || false);
    return result;
  }

  addStatePath(path: Pair<string, number>[], expanded: boolean) {
    if(!(path?.length > 0))
      return;
    let stateList: ECSNode<boolean>[] = this.getList(this.state, path, true);
    if(!stateList)
      return;
    const directiveList: ECSNode<CollapsibleDirective>[] = this.getList(this.directives, path, false) || [];
    let index: number = path[path.length - 1].b;
    if(index == null)
      index = stateList.length;
    stateList.splice(index, 0, { value: expanded });
    let wasExpanded: boolean = this.expandDirectiveNode(directiveList[index], false);
    const length: number = Math.min(stateList.length, directiveList.length);
    for(let i = index + 1; i < length; i++)
      wasExpanded = this.expandDirectiveNode(directiveList[i], wasExpanded);
  }

  removeStatePath(path: Pair<string, number>[]) {
    if(!(path?.length > 0))
      return;
    const index: number = path[path.length - 1].b;
    if(index == null)
      return;
    let stateList: ECSNode<boolean>[] = this.getList(this.state, path, false);
    if(!stateList)
      return;
    const directiveList: ECSNode<CollapsibleDirective>[] = this.getList(this.directives, path, false) || [];
    stateList.splice(index, 1);
    const length: number = Math.min(stateList.length + 1, directiveList.length);
    for(let i = index; i < length; i++)
      this.expandDirectiveNode(directiveList[i], stateList[i]);
  }

  register(path: Pair<string, number>[], directive: CollapsibleDirective) {
    if(!(path?.length > 0))
      return;
    let directives: ECStructure<CollapsibleDirective> = this.directives;
    for(let i = 0; i < path.length; i++) {
      const pair: Pair<string, number> = path[i];
      if(pair.b == null)
        return;
      let list: ECSNode<CollapsibleDirective>[] = directives[pair.a];
      if(!list) {
        list = [];
        directives[pair.a] = list;
      }
      if(i < path.length - 1) {
        const next: ECSNode<CollapsibleDirective> = list[pair.b];
        if(next?.child == null) {
          directives = {};
          if(next == null)
            list[pair.b] = { child: directives };
           else
            next.child = directives;
        } else
          directives = next.child;
      } else {
        if(list[pair.b] == null)
          list[pair.b] = { value: directive };
        else
          list[pair.b].value = directive;
      }
    }
    const expanded: boolean = this.getStateValue(path);
    directive.setExpanded(expanded == null ? false : expanded);
  }

  unregister(path: Pair<string, number>[], id: number) {
    let directiveList: ECSNode<CollapsibleDirective>[] = this.getList(this.directives, path, false);
    if(!directiveList)
      return;
    const stateList: ECSNode<boolean>[] = this.getList(this.state, path, false);
    const index: number = path[path.length - 1].b;
    if(index == null)
      return;
    const directive: ECSNode<CollapsibleDirective> = directiveList[index];
    if(directive?.value?.getId() == id)
      directiveList.splice(index, 1);
    if(stateList) {
      const length: number = Math.min(stateList.length, directiveList.length);
      for(let i = index; i < length; i++)
        directiveList[i].value.setExpanded(stateList[i].value);
    }
  }
}

@Directive({
  selector: '[collapsible]'
})
export class CollapsibleDirective implements OnInit, OnDestroy {
  private static nextId: number = 1;
  private id: number;
  private transitionId: number;
  private path: Pair<string, number>[];

  @Input() ecController: ExpandCollapseController;
  @Input() set collapsible(pathString: string) {
    this.path = ECPath.parse(pathString);
  }

  constructor(private element: ElementRef) {
    this.id = CollapsibleDirective.nextId++;
    this.transitionId = 0;
  }

  ngOnInit() {
    this.ecController.register(this.path, this);
  }

  ngOnDestroy() {
    this.ecController.unregister(this.path, this.id);
  }

  getId(): number {
    return this.id;
  }

  getExpanded(): boolean {
    return !this.element?.nativeElement?.classList.contains('collapsed');
  }

  setExpanded(expanded: boolean, animate: boolean = false) {
    let card: any = this.element?.nativeElement;
    if(!card)
      return;
    if(!animate) {
      this.transitionId++;
      card.classList.remove('transition');
      card.offsetHeight;
      if(expanded)
        card.classList.remove('collapsed');
      else
        card.classList.add('collapsed');
      return;
    }
    if(card.classList.contains('collapsed')) {
      if(expanded === false)
        return;
      card.style.height = card.offsetHeight + 'px';
      card.classList.remove('collapsed');
      if(!card.classList.contains('transition')) {
        card.classList.add('transition');
        const transitionId: number = ++this.transitionId;
        card.addEventListener('transitionend', () => {
          if(!card || transitionId != this.transitionId)
            return;
          card.classList.remove('transition');
          card.style.height = null;
        }, { once: true });
      }
      card.style.height = card.scrollHeight + 'px';
    } else {
      if(expanded === true)
        return;
      if(card.classList.contains('transition')) {
        card.style.height = null;
        card.classList.add('collapsed');
        return;
      }
      card.style.height = null;
      const fullHeight: number = card.offsetHeight;
      card.classList.add('collapsed');
      const collapsedHeight: number = card.offsetHeight;
      card.style.height = fullHeight + 'px';
      card.offsetHeight;
      card.classList.add('transition');
      const transitionId: number = ++this.transitionId;
      card.addEventListener('transitionend', () => {
        if(!card || transitionId != this.transitionId)
          return;
        card.classList.remove('transition');
        card.style.height = null;
      }, { once: true });
      card.style.height = collapsedHeight + 'px';
    }
  }
}